import { useState, useEffect } from 'react';

import { useTheme } from '@/theme';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { GenericLeaderboardData, GenericLeaderboardEntry } from '@/models/misc';
import { setIsLeaderboardOpen } from '@/state/features/app';
import { getGenericLeaderboardData } from '@/arise/api';
import { sendEvent } from '@/utils/analytics';
import { convertNumberToOrdinal, localiseNumber } from '@/utils/string';
import Modal from '@/components/Modal';

import * as styles from './styles.module.scss';

export default function GenericLeaderboard() {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isLeaderboardOpen = useAppSelector((state) => state.app.isLeaderboardOpen);
    const [leaderboardData, setLeaderboardData] = useState<GenericLeaderboardData>(null);
    const [isError, setIsError] = useState(false);

    async function getData() {
        try {
            const response = await getGenericLeaderboardData();
            setLeaderboardData(response);
        } catch (error) {
            console.warn('Could not get leaderboard data:', error);
            setIsError(true);
        }
    }

    useEffect(() => {
        if (isLeaderboardOpen) {
            getData();
            sendEvent('open_leaderboard');
        } else {
            setTimeout(() => {
                // Timeout to allow modal to move off screen
                setLeaderboardData(null);
                setIsError(false);
            }, 500);
        }
    }, [isLeaderboardOpen]);

    const hasLeaderboard = theme.customJSON?.leaderboard?.enabled;
    if (!hasLeaderboard) return null;

    return (
        <Modal
            modalClassname={styles.Modal}
            title="Leaderboard"
            isVisible={isLeaderboardOpen}
            onClose={() => {
                dispatch(setIsLeaderboardOpen(false));
            }}
            canScroll
            isLoading={!leaderboardData && !isError}
            leaveSpaceAboveAndBelow={false}
            zIndex={1000}
        >
            {leaderboardData || isError ? (
                <Leaderboard
                    entries={leaderboardData?.leaderboard}
                    emptyMessage="Once you complete the mission, your ranking will appear here!"
                    errorMessage="We’re having trouble loading the leaderboard right now."
                    isError={isError}
                />
            ) : null}
        </Modal>
    );
}

const Leaderboard = ({
    entries,
    emptyMessage,
    errorMessage,
    isError,
}: {
    entries: GenericLeaderboardEntry[];
    emptyMessage: string;
    errorMessage: string;
    isError: boolean;
}) => {
    if (isError) {
        return (
            <div className={styles.GenericLeaderboard}>
                <h3>{errorMessage ?? 'Try again later'}</h3>
            </div>
        );
    }

    if (!entries?.length) {
        return (
            <div className={styles.GenericLeaderboard}>
                <h3>{emptyMessage}</h3>
            </div>
        );
    }

    return (
        <div className={styles.GenericLeaderboard}>
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>User</th>
                        <th>Score</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map((entry, index) => (
                        <tr key={index}>
                            <td>{convertNumberToOrdinal(entry.position)}</td>
                            <td>
                                <span>{entry.displayName}</span>
                            </td>
                            <td>{localiseNumber(entry.score, 'N/A')}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
